/**
 * Semantic Subcategory & Category Icon Resolver
 * Provides contextual, rich, thematic emojis for every subcategory & genre across the entire platform.
 */

const KEYWORD_ICON_RULES: Array<{ pattern: RegExp; icon: string }> = [
  // 1. Mythical, Fantasy Creatures & Characters
  { pattern: /dragon|wyrm|drake|hydra|behemoth|farg/i, icon: "🐉" },
  { pattern: /elf|elven|drow|eldar|high-?elf|wood-?elf|night-?elf|altmer|dunmer|bosmer/i, icon: "🧝" },
  { pattern: /wizard|mage|sorcerer|warlock|spellcaster|archmage|thaumaturge/i, icon: "🧙" },
  { pattern: /witch|coven|hex|hag/i, icon: "🧙‍♀️" },
  { pattern: /demon|devil|fiend|abyssal|hellhound|succubus|incubus|diabolic|infernal/i, icon: "😈" },
  { pattern: /angel|seraph|cherub|archangel|celestial|divine|heavenly/i, icon: "🪽" },
  { pattern: /vampire|dracula|bloodsucker|nosferatu|vampiric/i, icon: "🧛" },
  { pattern: /werewolf|lycan|wolf|hound|canine|coyote|jackal/i, icon: "🐺" },
  { pattern: /orc|goblin|troll|ogre|hobgoblin|kobold|uruk/i, icon: "👺" },
  { pattern: /dwarf|dwarven|gnome|halfling|hobbit|mining|blacksmith/i, icon: "⛏️" },
  { pattern: /alien|extraterrestrial|ufo|martian|xenomorph|alderaan|altean|andorian|angara|amarr/i, icon: "👽" },
  { pattern: /mermaid|siren|merman|triton|aquatic-race|naga/i, icon: "🧜" },
  { pattern: /fairy|fae|pixie|nymph|sprite|dryad|sylph/i, icon: "🧚" },
  { pattern: /ghost|phantom|specter|wraith|apparition|banshee|poltergeist|haunted/i, icon: "👻" },
  { pattern: /zombie|undead|ghoul|walker|lich|mummy|skeleton|necromanc/i, icon: "💀" },
  { pattern: /giant|titan|colossus|goliath|cyclops|jotun/i, icon: "🗿" },
  { pattern: /superhero|hero|heroine|vigilante/i, icon: "🦸" },
  { pattern: /supervillain|villain|nemesis|arch-enemy/i, icon: "🦹" },
  { pattern: /knight|paladin|crusader|chevalier|templar|chivalry/i, icon: "🛡️" },
  { pattern: /ninja|shinobi|shadow-warrior/i, icon: "🥷" },
  { pattern: /samurai|ronin|bushi|daimyo/i, icon: "🥋" },
  { pattern: /pirate|buccaneer|corsair|swashbuckler|privateer|plunder/i, icon: "🏴‍☠️" },
  { pattern: /viking|berserker|norseman|raider|valkyrie/i, icon: "🪓" },
  { pattern: /assassin|hitman|rogue|thief|cutpurse|bounty-hunter/i, icon: "🗡️" },
  { pattern: /gladiator|warrior|fighter|champion|brawler|combatant/i, icon: "⚔️" },
  { pattern: /spy|agent|detective|undercover|investigator|inspector/i, icon: "🕵️" },
  { pattern: /robot|android|cyborg|automaton|mech|droid|ai-|synthetic/i, icon: "🤖" },
  { pattern: /steampunk|clockwork|gear|brass/i, icon: "⚙️" },
  { pattern: /cyberpunk|synth|hacker|neon|cyber|matrix/i, icon: "⚡" },
  { pattern: /god|goddess|deity|pantheon|olympian|divinity/i, icon: "⚡" },
  { pattern: /monster|chimera|kraken|leviathan|basilisk|cerberus|manticore|beast/i, icon: "👹" },
  { pattern: /dinosaur|raptor|t-rex|rex|fossil|prehistoric|jurassic/i, icon: "🦖" },

  // 2. Animals & Fauna
  { pattern: /phoenix|eagle|hawk|falcon|raven|crow|owl|bird|avian|griffin|gryphon/i, icon: "🦅" },
  { pattern: /lion|tiger|panther|leopard|cheetah|cat|feline|jaguar/i, icon: "🦁" },
  { pattern: /bear|ursine|grizzly|polar-bear/i, icon: "🐻" },
  { pattern: /horse|stallion|pegasus|unicorn|centaur|steed|equine/i, icon: "🦄" },
  { pattern: /snake|serpent|viper|cobra|reptile|lizard|gecko|iguana/i, icon: "🐍" },
  { pattern: /spider|arachnid|scorpion|insect|bug|ant|bee|wasp|beetle|anansi/i, icon: "🕷️" },
  { pattern: /amphibian|frog|toad|salamander/i, icon: "🐸" },
  { pattern: /fish|shark|whale|dolphin|aquatic|octopus|squid|crustacean/i, icon: "🐙" },
  { pattern: /pet|dog|puppy|kitten|bunny|rabbit|hamster/i, icon: "🐾" },

  // 3. Places, Settlements & Geography
  { pattern: /amusement-?park|theme-?park|carnival|fairground|roller-?coaster/i, icon: "🎡" },
  { pattern: /tavern|inn|pub|bar|brewery|saloon|speakeasy/i, icon: "🍺" },
  { pattern: /kingdom|empire|realm|dominion|dynasty|throne|palace|royal|coronation/i, icon: "👑" },
  { pattern: /castle|fortress|stronghold|citadel|bastion|keep|garrison/i, icon: "🏰" },
  { pattern: /dungeon|prison|jail|catacomb|crypt|vault|labyrinth|maze/i, icon: "🗝️" },
  { pattern: /city|metropolis|capital|urban|downtown/i, icon: "🏙️" },
  { pattern: /town|village|hamlet|settlement|outpost|borough/i, icon: "🏘️" },
  { pattern: /house|home|estate|manor|mansion|villa|cottage|cabin/i, icon: "🏡" },
  { pattern: /island|archipelago|atoll|isle|reef|cove|peninsula/i, icon: "🏝️" },
  { pattern: /mountain|peak|summit|alps|sierra|cliff|ridge|volcano/i, icon: "⛰️" },
  { pattern: /forest|wood|grove|jungle|rainforest|taiga|woods|wildwood/i, icon: "🌲" },
  { pattern: /river|stream|creek|brook|waterfall|canal/i, icon: "🌊" },
  { pattern: /lake|pond|lagoon|reservoir|bay|gulf|ocean|sea/i, icon: "🌊" },
  { pattern: /desert|dune|oasis|wasteland|badlands|savanna|steppe/i, icon: "🏜️" },
  { pattern: /swamp|marsh|bog|fen|mire|bayou|wetlands/i, icon: "🌿" },
  { pattern: /temple|shrine|sanctuary|pagoda|abbey|monastery/i, icon: "⛩️" },
  { pattern: /church|cathedral|chapel|basilica|altar/i, icon: "⛪" },
  { pattern: /mosque|minaret/i, icon: "🕌" },
  { pattern: /synagogue/i, icon: "🕍" },
  { pattern: /bridge|viaduct|aqueduct/i, icon: "🌉" },
  { pattern: /tower|spire|beacon|lighthouse|obelisk|monument|pillar/i, icon: "🗼" },
  { pattern: /library|archive|bookshop|study/i, icon: "📚" },
  { pattern: /school|academy|university|college|institute|guild|dojo/i, icon: "🎓" },
  { pattern: /hospital|clinic|infirmary|asylum|pharmacy|apothecary/i, icon: "🏥" },
  { pattern: /shop|store|market|bazaar|boutique|emporium|merchant/i, icon: "🛍️" },
  { pattern: /hotel|motel|resort|lodge|hostel/i, icon: "🏨" },
  { pattern: /casino|gambling|poker|roulette/i, icon: "🎰" },
  { pattern: /zoo|safari|wildlife/i, icon: "🦁" },
  { pattern: /museum|gallery|exhibition/i, icon: "🏛️" },
  { pattern: /planet|world|exoplanet|globe|terra/i, icon: "🪐" },
  { pattern: /galaxy|nebula|cosmos|universe|interstellar|cosmic/i, icon: "🌌" },
  { pattern: /star|sun|solar|constellation|nova|supernova/i, icon: "⭐" },
  { pattern: /moon|lunar|crescent|eclipse/i, icon: "🌙" },
  { pattern: /space-?station|spaceship|starship|spacecraft|rocket|shuttle/i, icon: "🚀" },
  { pattern: /cave|cavern|grotto|tunnel|chasm|abyss|crater/i, icon: "🕳️" },
  { pattern: /road|street|avenue|boulevard|highway|alley|lane/i, icon: "🛣️" },
  { pattern: /farm|ranch|barn|orchard|vineyard|pasture/i, icon: "🚜" },
  { pattern: /park|garden|plaza|square|meadow|botanical/i, icon: "🌳" },
  { pattern: /cemetery|graveyard|tomb|mausoleum|burial|grave/i, icon: "🪦" },
  { pattern: /stadium|arena|colosseum|racetrack/i, icon: "🏟️" },
  { pattern: /airport|airfield|heliport/i, icon: "✈️" },
  { pattern: /harbor|port|dock|marina/i, icon: "⚓" },
  { pattern: /train|railway|station|subway|metro/i, icon: "🚆" },

  // 4. Cultural & Real-World Historical Names
  { pattern: /greek|greece|athens|sparta|olympus/i, icon: "🏛️" },
  { pattern: /roman|rome|latin|caesar|legion/i, icon: "🏛️" },
  { pattern: /egypt|egyptian|pharaoh|nile|pyramid|sphinx/i, icon: "🏺" },
  { pattern: /celtic|gaelic|druid|avalon|stonehenge/i, icon: "🪨" },
  { pattern: /norse|scandinavian|swedish|norwegian|danish|finnish|icelandic/i, icon: "🪓" },
  { pattern: /japanese|japan|tokyo|kyoto/i, icon: "🗾" },
  { pattern: /chinese|china|beijing|han|dynasty/i, icon: "🏮" },
  { pattern: /korean|korea|seoul/i, icon: "🇰🇷" },
  { pattern: /indian|india|sanskrit|hindu|vedic|hindi/i, icon: "🪷" },
  { pattern: /arabic|arab|arabian|middle-?east|dubai|saudi|amazigh/i, icon: "🕌" },
  { pattern: /persian|iran|farsi|babylon|mesopotamia/i, icon: "🏺" },
  { pattern: /slavic|russian|ukrainian|poland|polish|czech|balkan|serbian|croatian|bulgarian/i, icon: "🪆" },
  { pattern: /french|france|paris|norman|gallic/i, icon: "🥖" },
  { pattern: /german|germany|prussian|bavarian|teutonic/i, icon: "🥨" },
  { pattern: /italian|italy|venice|florence|tuscan|sicilian/i, icon: "🍕" },
  { pattern: /spanish|spain|madrid|castilian|andalusian/i, icon: "💃" },
  { pattern: /portuguese|portugal|lisbon|brazilian/i, icon: "⛵" },
  { pattern: /irish|ireland|dublin|shamrock|leprechaun/i, icon: "☘️" },
  { pattern: /scottish|scotland|highlander|kilt|edinburgh/i, icon: "🏴󠁧󠁢󠁳󠁣󠁴󠁿" },
  { pattern: /english|british|london|anglo-?saxon|victorian|arthurian/i, icon: "👑" },
  { pattern: /welsh|wales/i, icon: "🐉" },
  { pattern: /native-?american|cherokee|navajo|apache|sioux|inuit|mohawk|tribal|indigenous/i, icon: "🪶" },
  { pattern: /african|nigeria|kenya|ghana|zulu|swahili|ethiopian|congo/i, icon: "🌍" },
  { pattern: /mayan|aztec|inca|mesoamerican/i, icon: "🗿" },
  { pattern: /albanian/i, icon: "🇦🇱" },
  { pattern: /algerian/i, icon: "🇩🇿" },
  { pattern: /amish/i, icon: "🌾" },
  { pattern: /australian|new-?zealand|maori/i, icon: "🦘" },
  { pattern: /canadian/i, icon: "🍁" },
  { pattern: /american|cowboy|western|outlaw/i, icon: "🤠" },
  { pattern: /hawaiian|polynesian|tahitian|samoan/i, icon: "🌺" },
  { pattern: /jewish|hebrew|yiddish|israel/i, icon: "🕎" },
  { pattern: /turkish|ottoman|istanbul/i, icon: "🇹🇷" },
  { pattern: /thai|thailand|siam/i, icon: "🛕" },
  { pattern: /vietnamese|vietnam/i, icon: "🇻🇳" },
  { pattern: /filipino|philippines/i, icon: "🇵🇭" },

  // 5. Pop Culture Franchises & RPGs
  { pattern: /harry-?potter|hogwarts|gryffindor|slytherin|ravenclaw|hufflepuff|quidditch/i, icon: "🪄" },
  { pattern: /star-?wars|jedi|sith|lightsaber|mandalorian|yoda|vader|tatooine/i, icon: "🌌" },
  { pattern: /dnd|d&d|dungeons-?and-?dragons|pathfinder|tabletop/i, icon: "🎲" },
  { pattern: /lord-?of-?the-?rings|lotr|middle-?earth|mordor|gondor|sauron/i, icon: "💍" },
  { pattern: /game-?of-?thrones|westeros|targaryen|stark|lannister|valyrian/i, icon: "👑" },
  { pattern: /marvel|avengers|spiderman|ironman|thor|mutant|x-men/i, icon: "🛡️" },
  { pattern: /dc-|batman|superman|gotham|metropolis/i, icon: "🦇" },
  { pattern: /pokemon|pokémon|pikachu/i, icon: "⚡" },
  { pattern: /warcraft|world-?of-?warcraft|azeroth|stormwind/i, icon: "⚔️" },
  { pattern: /elder-?scrolls|skyrim|morrowind|oblivion|tamriel/i, icon: "📜" },
  { pattern: /fallout|vault-tec|brotherhood-?of-?steel/i, icon: "☢️" },
  { pattern: /zelda|hyrule|triforce/i, icon: "🗡️" },
  { pattern: /anime|shonen|isekai|manga/i, icon: "🌸" },
  { pattern: /final-?fantasy|chocobo/i, icon: "🔮" },
  { pattern: /warhammer|40k|space-?marine/i, icon: "🪐" },
  { pattern: /mass-?effect|asari|turian|krogan/i, icon: "🚀" },
  { pattern: /star-?trek|klingon|vulcan|romulan|borg|federation/i, icon: "🖖" },

  // 6. Magic, Lore, Items, Flora & Equipment
  { pattern: /alchemy|potion|elixir|ingredient|brew|vial|concoction/i, icon: "🧪" },
  { pattern: /magic|spell|incantation|charm|hex|ritual|arcane|sorcery|mystic/i, icon: "🪄" },
  { pattern: /book|grimoire|tome|scroll|codex|journal|diary|novel|poem|manuscript|necronomicon/i, icon: "📖" },
  { pattern: /weapon|sword|blade|axe|spear|bow|arrow|dagger|shield|armor|mace|hammer/i, icon: "⚔️" },
  { pattern: /plant|flower|herb|blossom|rose|lily|orchid|lotus|flora|botanical/i, icon: "🌿" },
  { pattern: /tree|oak|willow|pine|redwood|forest-?plant/i, icon: "🌳" },
  { pattern: /disease|plague|illness|epidemic|virus|toxin|poison|venom|sickness/i, icon: "🦠" },
  { pattern: /food|dish|meal|cuisine|snack|recipe|fruit|bread|meat|soup|dessert/i, icon: "🍲" },
  { pattern: /drink|cocktail|potion-?descriptions|wine|beer|ale|beverage/i, icon: "🍹" },
  { pattern: /gem|crystal|jewel|diamond|ruby|sapphire|emerald|amethyst|mineral|ore/i, icon: "💎" },
  { pattern: /relic|artifact|treasure|amulet|talisman|ring|necklace|pendant/i, icon: "💍" },
  { pattern: /ship|boat|vessel|yacht|fleet|battleship|submarine|galley/i, icon: "⛵" },
  { pattern: /holiday|festival|celebration|feast|solstice/i, icon: "🎉" },
  { pattern: /guild|faction|clan|tribe|syndicate|cult|order|covenant|alliance/i, icon: "🚩" },
  { pattern: /clothing|outfit|costume|cloak|robe|uniform|dress/i, icon: "👘" },
  { pattern: /vehicle|car|truck|motorcycle|chariot|wagon|carriage/i, icon: "🚗" },
  { pattern: /music|instrument|band|song|melody|album|guitar|piano/i, icon: "🎵" },
  { pattern: /currency|coin|money|gold|bank|wealth/i, icon: "🪙" },
  { pattern: /weather|storm|thunder|lightning|blizzard|hurricane|tornado|rain|snow/i, icon: "⛈️" },
  { pattern: /fire|flame|blaze|inferno|ember|ash/i, icon: "🔥" },
  { pattern: /water|ice|frost|glacier|frozen/i, icon: "❄️" },
  { pattern: /wind|air|gale|breeze/i, icon: "💨" },
  { pattern: /earth|stone|rock|boulder/i, icon: "🪨" },
  { pattern: /shadow|darkness|void|night|twilight/i, icon: "🌑" },
  { pattern: /light|sun|radiance|dawn|glow/i, icon: "☀️" },
  { pattern: /love|romance|relationship|wedding|couple/i, icon: "💖" },
  { pattern: /business|company|brand|corporation|startup|agency/i, icon: "🏢" },
  { pattern: /baby|toddler|child|kid|newborn/i, icon: "👶" },
  { pattern: /description|lore|backstor|dialogue|quest|personality/i, icon: "📜" },
];

const CATEGORY_PALETTES: Record<string, string[]> = {
  "fantasy-names": ["🔮", "🗡️", "🛡️", "🧝", "🐉", "🪽", "🧙", "✨", "👑", "💎"],
  "real-names": ["🏛️", "🌍", "📜", "👑", "🏺", "🗺️", "🧭", "⚜️", "🪶"],
  "places-locations": ["🏰", "🏔️", "🏝️", "🌲", "🏙️", "🏘️", "🌉", "⛩️", "🏯"],
  "pop-culture": ["🎮", "⚔️", "🎲", "🚀", "🕹️", "👾", "🌌", "🎬", "🃏"],
  "other-names": ["⚔️", "💎", "🚩", "🪙", "🛡️", "📜", "🔥", "⚡"],
  "description-generators": ["📜", "📖", "🖋️", "🗺️", "🕯️", "🧭", "🔮"],
};

/**
 * Returns a rich, semantic emoji icon based on subcategory name, slug, and optional category.
 */
export function getSubcategoryIcon(
  slugOrName: string,
  categorySlug?: string
): string {
  if (!slugOrName) return "✨";

  const target = slugOrName.toLowerCase();

  // 1. Exact / Regex Pattern matching
  for (const rule of KEYWORD_ICON_RULES) {
    if (rule.pattern.test(target)) {
      return rule.icon;
    }
  }

  // 2. Stable fallback hashed from the subcategory name across category palette
  const palette =
    (categorySlug && CATEGORY_PALETTES[categorySlug]) ||
    CATEGORY_PALETTES["fantasy-names"];

  let hash = 0;
  for (let i = 0; i < target.length; i++) {
    hash = (hash * 31 + target.charCodeAt(i)) >>> 0;
  }

  return palette[hash % palette.length];
}
